import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "react-feather";
import { groupByCustomer, guardName } from "./dutyListModel";

const CUSTOMERS_PER_PAGE = 25;

const dayOfWeek = (year, month, day) =>
  new Date(year, month - 1, day).toLocaleDateString("en-GB", { weekday: "narrow" });

/**
 * The Detail view - the WinForms Duty List grid: one line per assignment,
 * one column per day, the guard's code in each cell. Grouped under a
 * customer heading instead of repeating the name on every line, Night lines
 * tinted, days nobody covered shaded, web-marked cells dotted (hover for the
 * time). Paged by customer so a whole-branch month stays quick on screen;
 * the print and Excel export always take every filtered line.
 */
const DutyListGrid = ({ data, rows, guards, onPickGuard }) => {
  const [page, setPage] = useState(0);
  const groups = useMemo(() => groupByCustomer(rows), [rows]);
  const pageCount = Math.max(1, Math.ceil(groups.length / CUSTOMERS_PER_PAGE));
  useEffect(() => setPage(0), [rows]);

  const days = useMemo(() => Array.from({ length: data.daysInMonth }, (_, i) => i + 1), [data.daysInMonth]);
  const pageGroups = groups.slice(page * CUSTOMERS_PER_PAGE, (page + 1) * CUSTOMERS_PER_PAGE);
  let srl = page === 0 ? 0 : groups.slice(0, page * CUSTOMERS_PER_PAGE).reduce((n, g) => n + g.rows.length, 0);

  if (rows.length === 0) {
    return <div className="text-center text-muted py-5">No assignments match these filters.</div>;
  }

  // Shown above the grid and again below it: the bottom-right corner sits
  // under the floating Quick Menu / Feedback buttons, so the bottom copy is
  // kept clear of them and the top copy is reachable without scrolling.
  const pager =
    pageCount > 1 ? (
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted">
          Customers {page * CUSTOMERS_PER_PAGE + 1}-{Math.min(groups.length, (page + 1) * CUSTOMERS_PER_PAGE)} of {groups.length}
        </span>
        <div className="btn-group">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(0)} title="First page">
            <ChevronsLeft size={14} />
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)} title="Previous page">
            <ChevronLeft size={14} />
          </button>
          <span className="btn btn-sm btn-outline-secondary disabled text-dark">
            Page {page + 1} of {pageCount}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} title="Next page">
            <ChevronRight size={14} />
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)} title="Last page">
            <ChevronsRight size={14} />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      {pager && <div className="d-flex justify-content-end mb-2 small">{pager}</div>}
      <div className="duty-grid-wrap">
        <table className="duty-grid">
          <thead>
            <tr>
              <th className="duty-grid-srl">#</th>
              <th className="duty-grid-name">Assignment</th>
              {days.map((d) => {
                const dow = dayOfWeek(data.year, data.month, d);
                return (
                  <th key={d} className={dow === "S" ? "is-weekend" : undefined}>
                    {d}
                    <small>{dow}</small>
                  </th>
                );
              })}
              <th className="duty-grid-total">Days</th>
            </tr>
          </thead>
          <tbody>
            {pageGroups.map((g) => (
              <React.Fragment key={g.custId}>
                <tr className="duty-grid-group">
                  <td colSpan={days.length + 3}>
                    <span className="duty-grid-group-name">{g.custName}</span>
                    <span className="text-muted ms-2">
                      {[...new Set(g.rows.map((r) => r.area).filter(Boolean))].join(", ")} · {g.rows.length} line{g.rows.length === 1 ? "" : "s"}
                    </span>
                  </td>
                </tr>
                {g.rows.map((r) => {
                  srl += 1;
                  return (
                    <tr key={r.key} className={r.shift === "Night" ? "is-night" : undefined}>
                      <td className="duty-grid-srl">{srl}</td>
                      <td className="duty-grid-name">
                        <div className="duty-grid-assignment" title={r.assignmentName}>
                          <span className={`duty-shift-pill ${r.shift === "Night" ? "night" : "day"}`}>{r.shift === "Night" ? "N" : "D"}</span>
                          {r.assignmentName}
                        </div>
                        <div className="duty-grid-flags">
                          {r.area && <span>{r.area}</span>}
                          {r.isTemp && <span className="flag-temp">Temp{r.toDate ? ` to ${r.toDate.slice(8, 10)}/${r.toDate.slice(5, 7)}` : ""}</span>}
                          {!r.isActive && <span className="flag-off">Switched off</span>}
                          {r.offShift && <span className="flag-off">Other shift</span>}
                        </div>
                      </td>
                      {days.map((d) => {
                        const code = r.codes[d - 1];
                        const expected = r.expected[d - 1];
                        const webTime = r.web[d];
                        const extra = r.extra[d];
                        let cls = code != null ? "is-filled" : expected ? "is-blank" : "is-off";
                        if (webTime !== undefined) cls += " is-web";
                        const title =
                          code != null
                            ? `${code} ${guardName(guards, code)}${webTime !== undefined ? `\nMarked on the web${webTime ? ` at ${webTime}` : ""}` : ""}${
                                extra ? `\nAlso: ${extra.map((e) => `${e} ${guardName(guards, e)}`).join(", ")}` : ""
                              }`
                            : expected
                            ? "Nobody on duty"
                            : undefined;
                        return (
                          <td
                            key={d}
                            className={cls}
                            title={title}
                            onClick={code != null ? () => onPickGuard(Number(code)) : undefined}
                          >
                            {code != null ? code : ""}
                            {extra && <sup>+{extra.length}</sup>}
                          </td>
                        );
                      })}
                      <td className="duty-grid-total">
                        {r.filledDays}
                        {r.blankDays > 0 && <small className="text-danger d-block">-{r.blankDays}</small>}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mt-2 small">
        <span className="duty-legend is-filled">23016</span> on duty
        <span className="duty-legend is-filled is-web">23016</span> marked on the web
        <span className="duty-legend is-blank" /> nobody on duty
        <span className="duty-legend is-off" /> not due
        {pager && (
          <div className="ms-auto" style={{ marginRight: 80 }}>
            {pager}
          </div>
        )}
      </div>
    </>
  );
};

export default DutyListGrid;
